import React, { useState, useEffect } from 'react';

const Settings = ({ onClose, selectedVoice, onVoiceChange, readCardAloud, onReadCardAloudChange, phoneticSoundsEnabled, onPhoneticSoundsChange }) => {
  const [availableVoices, setAvailableVoices] = useState([]);
  const [tempSelectedVoice, setTempSelectedVoice] = useState(selectedVoice);
  const [tempReadCardAloud, setTempReadCardAloud] = useState(readCardAloud);
  const [tempPhoneticSounds, setTempPhoneticSounds] = useState(phoneticSoundsEnabled);

  useEffect(() => {
    const loadVoices = () => {
      const voices = speechSynthesis.getVoices();
      setAvailableVoices(voices);
    };

    // Load voices immediately if available
    if (speechSynthesis.getVoices().length > 0) {
      loadVoices();
    }

    // Listen for voices to be loaded
    speechSynthesis.addEventListener('voiceschanged', loadVoices);
    
    return () => {
      speechSynthesis.removeEventListener('voiceschanged', loadVoices);
    };
  }, []);

  useEffect(() => {
    setTempSelectedVoice(selectedVoice);
  }, [selectedVoice]);

  useEffect(() => {
    setTempReadCardAloud(readCardAloud);
  }, [readCardAloud]);

  useEffect(() => {
    setTempPhoneticSounds(phoneticSoundsEnabled);
  }, [phoneticSoundsEnabled]);

  const handleVoiceChange = (voiceName) => {
    const voice = availableVoices.find(v => v.name === voiceName);
    setTempSelectedVoice(voice);
  };

  const handleSave = () => {
    onVoiceChange(tempSelectedVoice);
    onReadCardAloudChange(tempReadCardAloud);
    onPhoneticSoundsChange(tempPhoneticSounds);
    onClose();
  };

  const handleTestVoice = () => {
    if (tempSelectedVoice && 'speechSynthesis' in window) {
      // Stop any current speech
      speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance("Hello! This is a test of the voice selection.");
      utterance.voice = tempSelectedVoice;
      utterance.rate = 0.8;
      speechSynthesis.speak(utterance);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl p-6 md:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-kindergarten text-primary-700">
            ⚙️ Settings
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          {/* Voice Settings */}
          <div className="bg-gray-50 rounded-2xl p-6">
            <h3 className="text-xl font-kindergarten text-gray-800 mb-4">
              🎤 Voice Settings
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-lg font-kindergarten text-gray-700 mb-2">
                  Select Voice
                </label>
                <select
                  value={tempSelectedVoice?.name || ''}
                  onChange={(e) => handleVoiceChange(e.target.value)}
                  className="w-full px-4 py-3 border-4 border-primary-300 rounded-2xl text-lg focus:border-primary-500 focus:outline-none font-kindergarten"
                >
                  {availableVoices
                    .filter(voice => voice.lang.includes('en'))
                    .map((voice) => (
                      <option key={voice.name} value={voice.name}>
                        {voice.name} ({voice.lang})
                        {voice.name.includes('Emma') && ' 👑'}
                        {voice.name.includes('Microsoft') && ' 🪟'}
                      </option>
                    ))}
                </select>
                <p className="text-sm text-gray-600 font-kindergarten mt-2">
                  Choose a voice for reading flashcards. Microsoft Emma is recommended for children.
                </p>
              </div>

              <button
                onClick={handleTestVoice}
                className="btn-secondary w-full"
              >
                🔊 Test Voice
              </button>
            </div>
          </div>

          {/* Card Display Settings */}
          <div className="bg-green-50 rounded-2xl p-6">
            <h3 className="text-xl font-kindergarten text-gray-800 mb-4">
              📖 Card Display Options
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-lg font-kindergarten text-gray-700">
                    🔊 Read Card Aloud
                  </label>
                  <p className="text-sm text-gray-600 font-kindergarten mt-1">
                    Automatically read each card when displayed
                  </p>
                </div>
                <button
                  onClick={() => setTempReadCardAloud(!tempReadCardAloud)}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                    tempReadCardAloud ? 'bg-primary-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-200 ${
                      tempReadCardAloud ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <div className="bg-blue-50 rounded-xl p-3 mt-3">
                <p className="text-sm text-blue-700 font-kindergarten">
                  💡 <strong>How it works:</strong> When ON, cards are read aloud automatically. 
                  If Phonetic Sounds Mode is also ON, letters will sound out phonetically instead of saying the letter name.
                </p>
              </div>
              
              <div className="flex items-center justify-between mt-4">
                <div>
                  <label className="text-lg font-kindergarten text-gray-700">
                    🔤 Phonetic Sounds Mode
                  </label>
                  <p className="text-sm text-gray-600 font-kindergarten mt-1">
                    Enable phonetic reading for letters and words by default
                  </p>
                </div>
                <button
                  onClick={() => setTempPhoneticSounds(!tempPhoneticSounds)}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                    tempPhoneticSounds ? 'bg-primary-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-200 ${
                      tempPhoneticSounds ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <div className="bg-purple-50 rounded-xl p-3 mt-3">
                <p className="text-sm text-purple-700 font-kindergarten">
                  💡 <strong>How it works:</strong> When ON, Phonetic Sounds Mode will be enabled by default for all word lists. 
                  Letters will sound out phonetically (like "mmm" for M) to help with reading skills!
                </p>
              </div>
            </div>
          </div>

          {/* App Info */}
          <div className="bg-blue-50 rounded-2xl p-6">
            <h3 className="text-xl font-kindergarten text-gray-800 mb-4">
              📱 About
            </h3>
            <div className="space-y-2 text-gray-700">
              <p className="font-kindergarten">
                <strong>Version:</strong> 1.0.0
              </p>
              <p className="font-kindergarten">
                <strong>Features:</strong> Letters, Words, Math, Phonetic Sounds
              </p>
              <p className="font-kindergarten">
                <strong>Storage:</strong> Cloud-based with Firebase
              </p>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-yellow-50 rounded-2xl p-6">
            <h3 className="text-xl font-kindergarten text-gray-800 mb-4">
              💡 Tips
            </h3>
            <ul className="space-y-2 text-gray-700">
              <li className="font-kindergarten flex items-start">
                <span className="mr-2">•</span>
                <span>Use Phonetic Sounds Mode to help with reading skills</span>
              </li>
              <li className="font-kindergarten flex items-start">
                <span className="mr-2">•</span>
                <span>Your flashcard lists are saved automatically</span>
              </li>
              <li className="font-kindergarten flex items-start">
                <span className="mr-2">•</span>
                <span>Try different voices to find the best one for learning</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex space-x-4 mt-8">
          <button
            onClick={handleSave}
            className="btn-primary flex-1"
          >
            💾 Save Settings
          </button>
          <button
            onClick={onClose}
            className="btn-secondary flex-1"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
